import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ContentFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  sceneType: string;
  onSceneTypeChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
}

export function ContentFilters({
  search,
  onSearchChange,
  sceneType,
  onSceneTypeChange,
  status,
  onStatusChange,
}: ContentFiltersProps) {
  const { t } = useTranslation();

  const SCENE_TYPES = [
    { value: 'all', label: t('admin.content.sceneTypes.all') },
    { value: 'recording', label: t('admin.content.sceneTypes.recording') },
    { value: 'speech', label: t('admin.content.sceneTypes.speech') },
    { value: 'meeting', label: t('admin.content.sceneTypes.meeting') },
    { value: 'podcast', label: t('admin.content.sceneTypes.podcast') },
  ];

  const STATUSES = [
    { value: 'all', label: t('admin.content.status.all') },
    { value: 'active', label: t('admin.content.status.active') },
    { value: 'hidden', label: t('admin.content.status.hidden') },
    { value: 'flagged', label: t('admin.content.status.flagged') },
  ];
  return (
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      {/* Filter Options */}
      <div className="flex items-center gap-3 overflow-x-auto pb-1 shrink-0">
        {/* Scene Type Filter */}
        <div className="relative">
          <select
            value={sceneType}
            onChange={(e) => onSceneTypeChange(e.target.value)}
            className="appearance-none rounded-xl border-2 border-black bg-white pl-4 pr-10 h-10 w-[140px] font-bold shadow-[2px_2px_0_#1A1A1A] focus:outline-none cursor-pointer transition-all hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[1px_1px_0_#1A1A1A] whitespace-nowrap text-sm translate-y-[1px]"
          >
            {SCENE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Status Filter Buttons */}
        {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => onStatusChange(s.value)}
              className={cn(
                "whitespace-nowrap rounded-xl border-2 border-black px-4 h-10 text-sm font-bold transition-all flex items-center justify-center translate-y-[3px]",
                status === s.value
                  ? "bg-[#6FEDD6] shadow-[2px_2px_0_#1A1A1A] translate-x-0.5 translate-y-0.5"
                  : "bg-white hover:bg-gray-50 hover:shadow-[2px_2px_0_#1A1A1A] hover:translate-x-0.5 hover:translate-y-0.5",
                s.value === 'active' && "ml-10",
                s.value === 'all' && "w-[140px]"
              )}
            >
            {s.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative flex-1 md:max-w-md">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t('admin.content.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full rounded-xl border-2 border-black bg-white h-10 pl-12 pr-4 font-bold shadow-[2px_2px_0_#1A1A1A] focus:shadow-[4px_4px_0_#1A1A1A] focus:outline-none transition-all whitespace-nowrap text-sm"
        />
      </div>
    </div>
  );
}
